import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Search,
  ExternalLink,
  Building2,
  Info,
  ShieldCheck,
  AlertTriangle,
  BarChart3,
  Users,
  Clock,
  DollarSign,
  Percent,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import MainLayout from "@/components/layouts/MainLayout";

const ABNLookup = () => {
  return (
    <>
      <Helmet>
        <title>ABN Lookup | Numberz Insight</title>
        <meta
          name="description"
          content="Search and verify Australian Business Numbers (ABNs) with our ABN Lookup tool. Access business information from the Australian Business Register."
        />
        <meta
          name="keywords"
          content="ABN lookup, Australian Business Number, ABN search, business verification, ABR, Australian Business Register, Numberz Insight"
        />
      </Helmet>

      <MainLayout>
        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-16 overflow-hidden bg-gradient-to-b from-background to-background/80 dark:from-background dark:to-background/90">
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6 text-center md:text-left"
                >
                  <Badge className="mb-4 text-sm py-1 px-3" variant="outline">
                    Updated for 2025
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                    ABN Lookup
                  </h1>
                  <p className="text-xl text-muted-foreground">
                    Search and verify Australian Business Numbers (ABNs) to
                    confirm business details and GST registration status.
                  </p>
                  <div className="pt-4">
                    <Button size="lg" className="gap-2" asChild>
                      <a
                        href="https://abr.business.gov.au/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Start ABN Lookup <ArrowRight className="h-4 w-4 ml-1" />
                      </a>
                    </Button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="hidden md:block"
                >
                  <div className="relative rounded-lg overflow-hidden shadow-2xl border border-muted hover:border-primary/20 transition-colors duration-300">
                    <img
                      src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=100&w=2000&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      alt="Business professionals analyzing data on a laptop"
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Statistics Section */}
          <section className="py-16 bg-gradient-to-br from-primary/5 to-background dark:from-primary/10 dark:to-background">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-3xl font-bold mb-4"
                >
                  Why ABN Verification Matters
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-xl text-muted-foreground max-w-3xl mx-auto"
                >
                  The impact of proper business verification on your operations
                </motion.p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="bg-card dark:bg-gray-800 rounded-xl p-6 shadow-md border border-border dark:border-gray-700 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                    <Percent className="h-6 w-6" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">87%</h3>
                  <p className="text-muted-foreground">
                    of businesses check ABNs before engaging with new suppliers
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="bg-card dark:bg-gray-800 rounded-xl p-6 shadow-md border border-border dark:border-gray-700 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                    <Users className="h-6 w-6" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">2.5M+</h3>
                  <p className="text-muted-foreground">
                    active ABNs registered in the Australian Business Register
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="bg-card dark:bg-gray-800 rounded-xl p-6 shadow-md border border-border dark:border-gray-700 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                    <Clock className="h-6 w-6" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">28 Days</h3>
                  <p className="text-muted-foreground">
                    legal timeframe to update your ABN details after any changes
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="bg-card dark:bg-gray-800 rounded-xl p-6 shadow-md border border-border dark:border-gray-700 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                    <BarChart3 className="h-6 w-6" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">24/7</h3>
                  <p className="text-muted-foreground">
                    availability of the ABN Lookup service for business
                    verification
                  </p>
                </motion.div>
              </div>

              <div className="bg-card dark:bg-gray-800/80 rounded-xl p-8 shadow-lg border border-border dark:border-gray-700">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="md:w-1/4 flex justify-center">
                    <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                      <Award className="h-12 w-12 text-primary" />
                    </div>
                  </div>
                  <div className="md:w-3/4 text-center md:text-left">
                    <h3 className="text-xl font-bold mb-3">
                      Australian Taxation Office Recommendation
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      "The ATO strongly recommends verifying the ABN and GST
                      registration of all businesses you deal with. This helps
                      protect your business from fraud and ensures you can claim
                      GST credits appropriately."
                    </p>
                    <Button variant="outline" className="gap-2" asChild>
                      <a
                        href="https://www.ato.gov.au/business/gst/claiming-gst-credits/valid-tax-invoices-and-gsT-credits/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more from the ATO{" "}
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Official ABN Lookup Link Section */}
          <section className="py-12 bg-muted/30">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Search className="h-5 w-5 mr-2 text-primary" />
                    ABN Lookup - Official Service
                  </CardTitle>
                  <CardDescription>
                    Access the official Australian Business Register's ABN
                    Lookup service
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center p-6 bg-muted/50 rounded-lg">
                    <Info className="h-12 w-12 mx-auto text-primary mb-4" />
                    <h3 className="text-xl font-semibold mb-2">
                      Verify Business Details
                    </h3>
                    <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
                      ABN Lookup is the free public view of the Australian
                      Business Register (ABR). It provides access to publicly
                      available information supplied by businesses when they
                      register for an ABN.
                    </p>
                    <Button size="lg" className="gap-2" asChild>
                      <a
                        href="https://abr.business.gov.au/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Go to Official ABN Lookup{" "}
                        <ExternalLink className="h-4 w-4 ml-1" />
                      </a>
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium">Verify ABN Status</h4>
                        <p className="text-sm text-muted-foreground">
                          Check if a business has an active ABN registration
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium">
                          Confirm GST Registration
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Verify if a business is registered for GST
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium">Check Business Details</h4>
                        <p className="text-sm text-muted-foreground">
                          View entity type, business name, and location
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium">Verify Charity Status</h4>
                        <p className="text-sm text-muted-foreground">
                          Check DGR status and charity tax concessions
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Information Section */}
          <section className="py-16 bg-muted/30">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">About ABN Lookup</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Understanding the Australian Business Register and ABN
                  verification
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                <div className="relative rounded-lg overflow-hidden shadow-2xl border border-muted hover:border-primary/20 transition-colors duration-300 h-[300px]">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=100&w=2000&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    alt="Modern business buildings with glass facade"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="text-2xl font-bold">
                    Why ABN Verification Matters
                  </h3>
                  <p className="text-muted-foreground">
                    Verifying an Australian Business Number (ABN) is a crucial
                    step in establishing business relationships in Australia. It
                    helps confirm the legitimacy of a business, its GST
                    registration status, and provides important information
                    about the entity you're dealing with.
                  </p>
                  <p className="text-muted-foreground">
                    The ABN system, managed by the Australian Business Register
                    (ABR), provides transparency and accountability in the
                    business community. By using the ABN Lookup service, you can
                    access publicly available information about any registered
                    business in Australia.
                  </p>
                  <div className="pt-2">
                    <Button variant="outline" className="gap-2" asChild>
                      <a
                        href="https://abr.business.gov.au/Help/AbnLookup"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more about ABN Lookup{" "}
                        <ExternalLink className="h-4 w-4 ml-1" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      What is an ABN?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      An Australian Business Number (ABN) is a unique 11-digit
                      identifier issued by the Australian Business Register
                      (ABR) to businesses operating in Australia. It helps
                      businesses identify themselves for tax and other business
                      purposes.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Search className="h-5 w-5 mr-2 text-primary" />
                      ABN Lookup Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      ABN Lookup is the free public view of the Australian
                      Business Register. It provides access to publicly
                      available information supplied by businesses when they
                      register for an ABN.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Building2 className="h-5 w-5 mr-2 text-primary" />
                      Business Verification
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Verifying a business's ABN is essential before entering
                      into business relationships. It confirms the business is
                      registered, its GST status, and whether it's entitled to
                      tax concessions.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Common questions about ABN Lookup and business verification
                </p>
              </div>

              <div className="max-w-3xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger>
                      Why should I verify an ABN?
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Verifying an ABN is important for several reasons:
                      </p>
                      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                        <li>
                          Confirms the business is legitimately registered
                        </li>
                        <li>Verifies GST registration status</li>
                        <li>Helps prevent fraud and scams</li>
                        <li>Required for tax invoice compliance</li>
                        <li>Necessary for withholding tax obligations</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2">
                    <AccordionTrigger>
                      What information can I find with ABN Lookup?
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground">
                        ABN Lookup provides publicly available information about
                        businesses, including:
                      </p>
                      <ul className="list-disc pl-6 space-y-1 text-muted-foreground mt-2">
                        <li>ABN status (active or cancelled)</li>
                        <li>Entity name and type</li>
                        <li>GST registration status</li>
                        <li>Business location</li>
                        <li>Trading names (until October 31, 2025)</li>
                        <li>Deductible Gift Recipient (DGR) status</li>
                        <li>Charity tax concession status</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3">
                    <AccordionTrigger>
                      What's the difference between an ABN and ACN?
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground">
                        An Australian Business Number (ABN) is used for business
                        identification for tax purposes and is required for all
                        businesses. An Australian Company Number (ACN) is only
                        issued to companies registered under the Corporations
                        Act. Companies need both an ABN and ACN, while sole
                        traders, partnerships, and trusts only need an ABN.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4">
                    <AccordionTrigger>
                      Are trading names still displayed on ABN Lookup?
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground">
                        Yes, ABN Lookup will continue to display trading names
                        until October 31, 2025. After this date, only registered
                        business names will be displayed. This extended
                        transitional period gives businesses more time to
                        register their trading names as business names with ASIC
                        if they wish to continue using them.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5">
                    <AccordionTrigger>
                      How do I update my ABN details?
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground">
                        You cannot update your ABN details through ABN Lookup as
                        it's a read-only service. To update your ABN details,
                        you need to log in to the Australian Business Register
                        (ABR) at{" "}
                        <a
                          href="https://abr.gov.au"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          abr.gov.au
                        </a>
                        . It's important to keep your ABN details up-to-date, as
                        you're required to notify the ABR within 28 days of any
                        changes to your business details.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="py-16 bg-primary/5 dark:bg-primary/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Official Resources</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Helpful links to official government resources for business
                  verification
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      ABN Lookup
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      The official ABN Lookup service provided by the Australian
                      Business Register.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://abr.business.gov.au/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit ABN Lookup{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Australian Business Register
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Update your ABN details and manage your business
                      registrations.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://abr.gov.au/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit ABR <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Business.gov.au
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Government information and services to help your business
                      succeed.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://business.gov.au/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit Business.gov.au{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-gradient-to-br from-background to-muted/30">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1">
                  <div className="relative rounded-lg overflow-hidden shadow-2xl border border-muted hover:border-primary/20 transition-colors duration-300 mb-6 md:mb-0">
                    <img
                      src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?q=100&w=2000&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      alt="Business professionals discussing documents"
                      className="w-full h-[300px] object-cover"
                    />
                  </div>
                  <Card className="border-primary/20 shadow-lg">
                    <CardHeader>
                      <CardTitle>Get Professional Assistance</CardTitle>
                      <CardDescription>
                        Contact us for help with your business registration
                        needs
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <Button className="w-full" size="lg" asChild>
                          <Link to="/abn-registration">
                            ABN Registration Services
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          size="lg"
                          asChild
                        >
                          <Link to="/contact">Contact Us</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className="order-1 md:order-2">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h2 className="text-3xl font-bold mb-4">
                      Need Help with Business Registration?
                    </h2>
                    <p className="text-xl text-muted-foreground mb-6">
                      Our team of professionals can assist you with ABN
                      registration, business name registration, and other
                      business setup services.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                        <span>Expert guidance on business registration</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                        <span>Fast and efficient service</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                        <span>Ongoing support for your business</span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </MainLayout>
    </>
  );
};

export default ABNLookup;
